import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PerfectScrollbar from 'react-perfect-scrollbar'
import dayjs from 'dayjs'
import {
  SubTitleSelectionStyle,
  TitleSelectionStyle,
} from '../ReadershipProfileStyles'
import {
  CompareGroupType,
  StudyObjectGroupCompareEnum,
  StudyObjectType,
} from '../configs/StudiesObjectsCompareGroupTypesConfig'
import { ReactComponent as BookIcon } from '../../../../../assets/icons/app/book.icon.svg'
import { ReactComponent as AuthorIcon } from '../../../../../assets/icons/app/author.icon.svg'
import { ReactComponent as SegmentIcon } from '../../../../../assets/icons/app/segment.icon.svg'
import DynamicsResultsFilterActionTag from '../../../../dynamics-results/filters/actions-filters/DynamicsResultsFilterActionTag'
import GlSlideToggleWithText from '../../../../share/GlSlideToggleWithText'
import GlCheckBoxWithText from '../../../../share/GlCheckBoxWithText'
import { TagColorEnum } from '../../../../../core/enums/tag-color.enum'
import AnalyticDateMonthFilters from '../../../AnalyticDateMonthFilters'
import {
  CompareGroupValueType,
  StudyObjectValueType,
} from '../../../../../store/interfaces/analytics-details/new-readership-profile-state.interface'
import { AuthorInterface } from '../../../../../core/interface/author.interface'
import { BookInterface } from '../../../../../core/interface/book.interface'
import { ReadershipProfilePreviewUsersInputInterface } from '../../../../../core/interface/analytics/readership-profile-preview-users-input.interface'
import NewReadershipProfileBookFiltersInterface from '../../../../../core/interface/analytics/new-readership-profile-book-filters'

const DetailsSelection = ({
  title,
  type,
  value,
  selectionType,
  filtersActions,
  userFilters,
  onChangeFiltersPayload,
}: {
  title: string
  type: StudyObjectType | CompareGroupType
  value: StudyObjectValueType | CompareGroupValueType
  selectionType: 'studyObjectValue' | 'compareGroupValue'
  filtersActions: NewReadershipProfileBookFiltersInterface
  userFilters: ReadershipProfilePreviewUsersInputInterface
  onChangeFiltersPayload: (
    filters: ReadershipProfilePreviewUsersInputInterface,
  ) => void
}) => {
  const { t } = useTranslation()

  const [toggleValueTag, setToggleValueTag] = useState<boolean>(
    userFilters.tags.length > 0,
  )
  const [toggleValueAllBooksInLibrary, setToggleValueAllBooksInLibrary] =
    useState<boolean>(
      type === StudyObjectGroupCompareEnum.TITLE &&
        !!userFilters.min_books_ownd_in_input,
    )
  const [toggleValueDateBooksInLibrary, setToggleValueDateBooksInLibrary] =
    useState<boolean>(!!userFilters.added_in_library_first_date)
  const [toggleValueNumberBooksInLibrary, setToggleValueNumberBooksInLibrary] =
    useState<boolean>(!!userFilters.min_books_ownd_in_input)

  const [checkboxValueTagOwn, setCheckboxValueTagOwn] = useState(
    userFilters.tags.includes('own'),
  )
  const [checkboxValueTagWishlist, setCheckboxValueTagWishlist] = useState(
    userFilters.tags.includes('wish'),
  )
  const [checkboxValueTagReading, setCheckboxValueTagReading] = useState(
    userFilters.tags.includes('reading'),
  )
  const [checkboxValueTagRead, setCheckboxValueTagRead] = useState(
    userFilters.tags.includes('read'),
  )
  const [checkboxValueTagLike, setCheckboxValueTagLike] = useState(
    userFilters.tags.includes('like'),
  )

  const [inputValueNumberBooksLibrary, setInputValueNumberBooksLibrary] =
    useState<number>(userFilters.min_books_ownd_in_input || 1)

  const current = dayjs().subtract(1, 'month')
  const startDate = userFilters.added_in_library_first_date
    ? dayjs(userFilters.added_in_library_first_date)
    : current
  const [
    selectValueDateStartBooksInLibrary,
    setSelectValueDateStartBooksInLibrary,
  ] = useState({
    end: startDate.endOf('month').toISOString(),
    start: startDate.startOf('month').toISOString(),
  })
  const endDate = userFilters.added_in_library_last_date
    ? dayjs(userFilters.added_in_library_last_date)
    : current
  const [
    selectValueDateEndBooksInLibrary,
    setSelectValueDateEndBooksInLibrary,
  ] = useState({
    end: endDate.endOf('month').toISOString(),
    start: endDate.startOf('month').toISOString(),
  })

  const typeIcon = useMemo(() => {
    switch (type) {
      case StudyObjectGroupCompareEnum.TITLE:
        return <BookIcon className="w-6 h-6" />
      case StudyObjectGroupCompareEnum.AUTHOR:
        return <AuthorIcon className="w-6 h-6" />
      case StudyObjectGroupCompareEnum.CLIL:
        return <SegmentIcon className="w-6 h-6" />
      default:
        return null
    }
  }, [type])

  const typeLabel = useMemo(() => {
    switch (type) {
      case StudyObjectGroupCompareEnum.TITLE:
        return t('works')
      case StudyObjectGroupCompareEnum.AUTHOR:
        return t('authors')
      case StudyObjectGroupCompareEnum.CLIL:
        return t('clils')
      case StudyObjectGroupCompareEnum.USERS_LIST:
        return t('audience')
      default:
        return ''
    }
  }, [type])

  const hasFilters = useMemo(() => {
    if (!filtersActions) return false

    return Object.entries(filtersActions).some(([key, value]) => {
      if (!value) return false

      if (Array.isArray(value)) {
        if (key === 'publishing_date') {
          return value.some((item) => item !== null)
        }
        return value.length > 0
      }

      return false
    })
  }, [filtersActions])

  const hasTagChecked = useMemo(() => {
    return (
      checkboxValueTagOwn ||
      checkboxValueTagWishlist ||
      checkboxValueTagReading ||
      checkboxValueTagRead ||
      checkboxValueTagLike
    )
  }, [
    checkboxValueTagOwn,
    checkboxValueTagWishlist,
    checkboxValueTagReading,
    checkboxValueTagRead,
    checkboxValueTagLike,
  ])

  const isDateRangeValid = useMemo(() => {
    if (!toggleValueDateBooksInLibrary) return true
    const start = dayjs(selectValueDateStartBooksInLibrary.start)
    const end = dayjs(selectValueDateEndBooksInLibrary.end)
    return !start.isAfter(end, 'day')
  }, [
    toggleValueDateBooksInLibrary,
    selectValueDateStartBooksInLibrary,
    selectValueDateEndBooksInLibrary,
  ])

  useEffect(() => {
    const payload: ReadershipProfilePreviewUsersInputInterface = {
      type: selectionType,
      added_in_library_first_date: null,
      added_in_library_last_date: null,
      min_books_ownd_in_input: null,
      tags: [],
    }

    if (toggleValueTag) {
      const tags: ('own' | 'wish' | 'reading' | 'read' | 'like')[] = []
      if (checkboxValueTagOwn) tags.push('own')
      if (checkboxValueTagWishlist) tags.push('wish')
      if (checkboxValueTagReading) tags.push('reading')
      if (checkboxValueTagRead) tags.push('read')
      if (checkboxValueTagLike) tags.push('like')

      if (tags.length > 0) {
        payload.tags = tags
      }
    }

    if (toggleValueDateBooksInLibrary && isDateRangeValid) {
      payload.added_in_library_first_date = dayjs(
        selectValueDateStartBooksInLibrary.start,
      ).format('YYYY-MM-DD')
      payload.added_in_library_last_date = dayjs(
        selectValueDateEndBooksInLibrary.end,
      ).format('YYYY-MM-DD')
    }

    if (toggleValueNumberBooksInLibrary) {
      payload.min_books_ownd_in_input = inputValueNumberBooksLibrary
    }

    if (toggleValueAllBooksInLibrary) {
      payload.min_books_ownd_in_input = (value as BookInterface[]).length
    }

    onChangeFiltersPayload(payload)
  }, [
    toggleValueTag,
    checkboxValueTagOwn,
    checkboxValueTagWishlist,
    checkboxValueTagReading,
    checkboxValueTagRead,
    checkboxValueTagLike,
    toggleValueDateBooksInLibrary,
    selectValueDateStartBooksInLibrary,
    selectValueDateEndBooksInLibrary,
    toggleValueNumberBooksInLibrary,
    inputValueNumberBooksLibrary,
    toggleValueAllBooksInLibrary,
    isDateRangeValid,
  ])

  return (
    <div className="relative basis-1/2 h-full flex flex-col gap-6">
      <div className="flex-none flex flex-col">
        <TitleSelectionStyle>{title}</TitleSelectionStyle>
        <SubTitleSelectionStyle className="flex flex-row items-center gap-2">
          {typeIcon}
          {typeLabel}
        </SubTitleSelectionStyle>
      </div>
      {hasFilters &&
        (type === StudyObjectGroupCompareEnum.TITLE ||
          type === StudyObjectGroupCompareEnum.AUTHOR) && (
          <PerfectScrollbar className="flex flex-row flex-wrap items-center gap-2 max-h-[80px]">
            <span>{`${t('detailsReadershipProfile.filterUsedRecap')} :`}</span>
            {Object.keys(filtersActions).map((columnKey: string) => (
              <DynamicsResultsFilterActionTag
                key={columnKey}
                columnKey={columnKey}
                filtersActions={filtersActions}
                color="#252525"
                disableRemoveButton
              />
            ))}
          </PerfectScrollbar>
        )}
      {type === StudyObjectGroupCompareEnum.USERS_LIST && (
        <p>{t('detailsReadershipProfile.noFiltersAvailable')}</p>
      )}
      {type === StudyObjectGroupCompareEnum.TITLE &&
        (value as BookInterface[]).length > 1 && (
          <GlSlideToggleWithText
            id="allBooksInLibrayToggle"
            label={t('detailsReadershipProfile.filterUserAllBooksLibrary')}
            value={toggleValueAllBooksInLibrary}
            handleOnToggle={() =>
              setToggleValueAllBooksInLibrary(!toggleValueAllBooksInLibrary)
            }
            noJustify
          />
        )}
      {(type === StudyObjectGroupCompareEnum.TITLE ||
        type === StudyObjectGroupCompareEnum.AUTHOR) && (
        <div>
          <GlSlideToggleWithText
            id="tagToggle"
            label={t('detailsReadershipProfile.filterTagToggle')}
            value={toggleValueTag}
            handleOnToggle={() => setToggleValueTag(!toggleValueTag)}
            noJustify
          />
          <div className="mt-2">
            {toggleValueTag && (
              <p
                className="text-xs"
                style={{ color: hasTagChecked ? '#929292' : '#FF0000' }}
              >
                {t('detailsReadershipProfile.selectOneTag')}
              </p>
            )}
            <div className="flex flex-row gap-2 items-center">
              <GlCheckBoxWithText
                text={t('tag.own')}
                color={TagColorEnum.OWN}
                checked={checkboxValueTagOwn}
                handleOnCheckedChange={() =>
                  setCheckboxValueTagOwn(!checkboxValueTagOwn)
                }
                disabled={!toggleValueTag}
              />
              <GlCheckBoxWithText
                text={t('tag.wishlist')}
                color={TagColorEnum.WISHLIST}
                checked={checkboxValueTagWishlist}
                handleOnCheckedChange={() =>
                  setCheckboxValueTagWishlist(!checkboxValueTagWishlist)
                }
                disabled={!toggleValueTag}
              />
              <GlCheckBoxWithText
                text={t('tag.reading')}
                color={TagColorEnum.READING}
                checked={checkboxValueTagReading}
                handleOnCheckedChange={() =>
                  setCheckboxValueTagReading(!checkboxValueTagReading)
                }
                disabled={!toggleValueTag}
              />
              <GlCheckBoxWithText
                text={t('tag.read')}
                color={TagColorEnum.READ}
                checked={checkboxValueTagRead}
                handleOnCheckedChange={() =>
                  setCheckboxValueTagRead(!checkboxValueTagRead)
                }
                disabled={!toggleValueTag}
              />
              <GlCheckBoxWithText
                text={t('tag.like')}
                color={TagColorEnum.LIKE}
                checked={checkboxValueTagLike}
                handleOnCheckedChange={() =>
                  setCheckboxValueTagLike(!checkboxValueTagLike)
                }
                disabled={!toggleValueTag}
              />
            </div>
          </div>
        </div>
      )}
      {type !== StudyObjectGroupCompareEnum.USERS_LIST &&
        type !== StudyObjectGroupCompareEnum.CLIL && (
          <div>
            <GlSlideToggleWithText
              id="dateBooksInLibraryToggle"
              label={t(
                'detailsReadershipProfile.filterDateBooksInLibraryToggle',
              )}
              value={toggleValueDateBooksInLibrary}
              handleOnToggle={() =>
                setToggleValueDateBooksInLibrary(!toggleValueDateBooksInLibrary)
              }
              noJustify
            />
            <div className="mt-2">
              {toggleValueDateBooksInLibrary && !isDateRangeValid && (
                <p className="text-xs text-red-600">
                  {t('detailsReadershipProfile.invalidDateRange')}
                </p>
              )}
              <div className="flex flex-row flex-wrap gap-2 items-center">
                <p
                  className={
                    !toggleValueDateBooksInLibrary
                      ? 'text-[#929292]'
                      : 'text-[#252525]'
                  }
                >
                  {t('detailsReadershipProfile.dateBooksInLibrary')}
                </p>
                <AnalyticDateMonthFilters
                  handleChangeQuery={(key, value) => {
                    setSelectValueDateStartBooksInLibrary(value)
                  }}
                  startDateSelected={selectValueDateStartBooksInLibrary.start}
                  endDateSelected={selectValueDateStartBooksInLibrary.end}
                  menuKey="dateBooksInLibrary_START"
                  disabled={!toggleValueDateBooksInLibrary}
                />
                <p
                  className={
                    !toggleValueDateBooksInLibrary
                      ? 'text-[#929292]'
                      : 'text-[#252525]'
                  }
                >
                  {t('and')}
                </p>
                <AnalyticDateMonthFilters
                  handleChangeQuery={(key, value) => {
                    setSelectValueDateEndBooksInLibrary(value)
                  }}
                  startDateSelected={selectValueDateEndBooksInLibrary.start}
                  endDateSelected={selectValueDateEndBooksInLibrary.end}
                  menuKey="dateBooksInLibrary_END"
                  disabled={!toggleValueDateBooksInLibrary}
                />
              </div>
            </div>
          </div>
        )}
      {(type === StudyObjectGroupCompareEnum.AUTHOR ||
        type === StudyObjectGroupCompareEnum.CLIL) && (
        <div>
          <GlSlideToggleWithText
            id="numberBooksInLibraryToggle"
            label={t(
              'detailsReadershipProfile.filterNumberBooksInLibraryToggle',
            )}
            value={toggleValueNumberBooksInLibrary}
            handleOnToggle={() =>
              setToggleValueNumberBooksInLibrary(
                !toggleValueNumberBooksInLibrary,
              )
            }
            noJustify
          />
          <div className="mt-2 flex flex-row gap-2 items-center">
            <p
              className={
                !toggleValueNumberBooksInLibrary
                  ? 'text-[#929292]'
                  : 'text-[#252525]'
              }
            >
              {t('detailsReadershipProfile.usersHave')}
            </p>
            <input
              className={`w-20 text-center user-select-none p-2 bg-[#F7F7F7] rounded outline-none text-base [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                !toggleValueNumberBooksInLibrary
                  ? 'text-[#929292]'
                  : 'text-[#252525]'
              }`}
              type="number"
              value={inputValueNumberBooksLibrary}
              onChange={(e) =>
                setInputValueNumberBooksLibrary(Number(e.target.value))
              }
              min={1}
              step={1}
              disabled={!toggleValueNumberBooksInLibrary}
            />
            <p
              className={
                !toggleValueNumberBooksInLibrary
                  ? 'text-[#929292]'
                  : 'text-[#252525]'
              }
            >
              {t(
                type === StudyObjectGroupCompareEnum.AUTHOR
                  ? 'detailsReadershipProfile.booksOfAuthorsInLibrary'
                  : 'detailsReadershipProfile.booksOfClilsInLibrary',
                { count: (value as AuthorInterface[] | string[]).length },
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default DetailsSelection
